<template>
    <ion-list>
        <page-header :title="$t('signup.title')" />
        <p class="error-message">{{ error }}</p>
        <ion-grid>
            <ion-row>
                <ion-col v-if="!user" size-xs="12" size-sm="12" size-md="6">
                    <text-box :label="$t('signup.username')" inputType="text" @onChange="handleUsernameChange" :error="errors['name']" />
                </ion-col>
                <ion-col v-if="!user" size-xs="12" size-sm="12" size-md="6">
                    <text-box :label="$t('master.email')" inputType="email" @onChange="handleEmailChange" :error="errors['email']" />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col v-if="!user" size-xs="12" size-sm="12" size-md="6">
                    <text-box
                        :label="$t('master.password')"
                        inputType="password"
                        @onChange="handlePasswordChange"
                        :error="errors['password']"
                    />
                </ion-col>
                <ion-col
                    v-if="!user || (user && !user.options.find(option => option.type == 'gender'))"
                    size-xs="12"
                    size-sm="12"
                    size-md="6"
                >
                    <options-selector
                        :options="genderOptions"
                        :label="$t('options.gender')"
                        @onChange="handleChange"
                        @onClear="handleClear"
                        type="gender"
                        :error="errors['gender']"
                        required="true"
                    />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col size-xs="12" size-sm="12" size-md="6">
                    <options-selector
                        :options="maritalStatusOptions"
                        :label="$t('options.maritalStatus')"
                        @onChange="handleChange"
                        @onClear="handleClear"
                        type="marital-status"
                        :error="errors['maritalStatus']"
                        required="true"
                    />
                </ion-col>
                <ion-col size-xs="12" size-sm="12" size-md="6">
                    <options-selector
                        :options="kidsOptions"
                        :label="$t('options.kids')"
                        @onChange="handleChange"
                        @onClear="handleClear"
                        type="kids"
                        :error="errors['kids']"
                        required="true"
                    />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col size-xs="12" size-sm="12" size-md="6">
                    <options-selector
                        :options="purposeOptions"
                        :label="$t('options.purpose')"
                        @onChange="handleChange"
                        @onClear="handleClear"
                        type="purpose"
                        :error="errors['purpose']"
                        required="true"
                    />
                </ion-col>
                <ion-col size-xs="12" size-sm="12" size-md="6">
                    <options-selector
                        :options="ageOptions"
                        :label="$t('options.age')"
                        @onChange="handleChange"
                        @onClear="handleClear"
                        type="age"
                        :error="errors['age']"
                        required="true"
                    />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col size-xs="12" size-sm="12" size-md="6">
                    <options-selector
                        :options="countryOptions"
                        :label="$t('options.country')"
                        @onChange="handleChange"
                        @onClear="handleClear"
                        type="country"
                        :error="errors['country']"
                        required="true"
                    />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col size-xs="12" size-sm="12" size-md="12">
                    <text-box
                        :label="$t('signup.describeYourself')"
                        inputType="text"
                        @onChange="handleDescribeYourselfChange"
                        :error="errors['describeYourself']"
                        :optionId="describeYourselfOptionId"
                        rows="2"
                    />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col size-xs="12" size-sm="12" size-md="12">
                    <text-box
                        :label="$t('signup.describeOthers')"
                        inputType="text"
                        @onChange="handleDescribeOthersChange"
                        :error="errors['describeOthers']"
                        :optionId="describeOthersOptionId"
                        rows="2"
                    />
                </ion-col>
            </ion-row>
        </ion-grid>
        <router-link to="/terms">
            <ion-item>
                <span color="secondary">
                    {{ $t('master.acceptAgreement') }}
                </span>
            </ion-item>
        </router-link>
        <ion-button class="ion-float-end" @click="handleSignup" shape="round" strong>
            {{ $t('master.next') }}
            <img class="icon-next icon-img-xs" src="@/assets/icon-images/arrow-right-white.png" />
        </ion-button>
    </ion-list>
</template>

<script>
import OptionsSelector from '@/components/OptionsSelector'
import TextBox from '@/components/TextBox'
import AxiosController from '@/controllers/AxiosController'
import { validateSignup } from '@/utils/validators.js'
import PageHeader from '@/components/PageHeader'
import { mapGetters } from 'vuex'
import { isMobile } from 'mobile-device-detect'
import { getAnalytics, logEvent } from 'firebase/analytics'

export default {
    components: {
        OptionsSelector,
        TextBox,
        PageHeader
    },
    name: 'RegisterOne',
    computed: mapGetters(['user', 'lang']),
    data() {
        return {
            genderOptions: [],
            countryOptions: [],
            maritalStatusOptions: [],
            purposeOptions: [],
            ageOptions: [],
            kidsOptions: [],
            selectedOptions: {},
            username: '',
            email: '',
            password: '',
            describeYourselfOptionId: '',
            describeOthersOptionId: '',
            describeYourself: '',
            describeOthers: '',
            errors: [],
            error: '',
            isMobile
        }
    },
    mounted() {
        const params = {
            type: ['gender', 'country', 'marital-status', 'purpose', 'age', 'kids', 'describe-yourself', 'describe-others']
        }
        AxiosController.get('/options', { params }).then(res => {
            this.genderOptions = res.data.filter(option => option.type === 'gender')
            this.countryOptions = res.data.filter(option => option.type === 'country')
            this.maritalStatusOptions = res.data.filter(option => option.type === 'marital-status')
            this.kidsOptions = res.data.filter(option => option.type === 'kids')
            this.purposeOptions = res.data.filter(option => option.type === 'purpose')
            this.ageOptions = res.data.filter(option => option.type === 'age')
            this.describeYourselfOptionId = res.data.find(option => option.type === 'describe-yourself').id
            this.describeOthersOptionId = res.data.find(option => option.type === 'describe-others').id
        })
    },
    methods: {
        handleChange: function(option) {
            this.selectedOptions[option.type] = option.value
        },
        handleClear: function(option) {
            delete this.selectedOptions[option.type]
        },
        handleUsernameChange: function(value) {
            this.username = value
        },
        handleEmailChange(value) {
            this.email = value
        },
        handlePasswordChange(value) {
            this.password = value
        },
        handleDescribeYourselfChange(value) {
            this.describeYourself = value
        },
        handleDescribeOthersChange(value) {
            this.describeOthers = value
        },
        handleSignup: function() {
            this.errors = []
            this.error = ''

            const { error } = validateSignup({
                name: this.user ? this.user.name : this.username,
                email: this.user ? this.user.email : this.email,
                password: this.user ? 'randpassword' : this.password,
                gender: this.selectedOptions['gender'],
                maritalStatus: this.selectedOptions['marital-status'],
                kids: this.selectedOptions['kids'],
                purpose: this.selectedOptions['purpose'],
                age: this.selectedOptions['age'],
                country: this.selectedOptions['country'],
                describeYourself: this.describeYourself.data,
                describeOthers: this.describeOthers.data
            })
            if (error) {
                error.details.forEach(err => {
                    this.$set(this.errors, err.path[0], err.message)
                })
                return
            }

            const descriptionDetails = [this.describeOthers, this.describeYourself]

            const analytics = getAnalytics()
            logEvent(analytics, 'sign_up')
            if (this.selectedOptions['gender'] == 2) {
                logEvent(analytics, 'sign_up_female')
            } else {
                logEvent(analytics, 'sign_up_male')
            }
            this.$gtag.event('sign_up')
            this.$gtag.event('conversion', { send_to: 'AW-929662769/XhRPCN_Z5GMQsY6muwM' })

            if (!this.user) {
                this.$store.dispatch('signup', {
                    name: this.username,
                    password: this.password,
                    password_confirmation: this.password,
                    email: this.email,
                    options: this.selectedOptions,
                    details: descriptionDetails,
                    lang: this.lang
                })
            } else {
                this.$store.dispatch('signup', {
                    userId: this.user.id,
                    options: this.selectedOptions,
                    details: descriptionDetails
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
ion-button {
    margin-top: 20px;
    --background: #7cc18b;
    --color: #000000;
}

#app[dir='rtl'] .icon-next {
    transform: rotate(180deg);
}

@media (min-width: 768px) {
    ion-button {
        margin: 30px;
    }
}
</style>
